import React, {useCallback} from 'react';
import b_ from 'b_';

import I18N from 'assets/i18n/brandlift-project';

import {Button} from 'client/common/components/lego';

import {ModalLevel, useModal} from 'client/common/hooks/modal';
import {useDevice} from 'client/common/hooks/device';

import {Device} from 'client/common/utils/device-width';

import './mobile-message.css';

const b = b_.with('mobile-message');

const i18n = I18N.common.mobileMessage;

interface MobileMessageProps {
    hideModal: (beforeClose?: () => false | void) => void;
    title: string;
    description: string;
}

function MobileMessage(props: MobileMessageProps) {
    const {description, hideModal, title} = props;

    return (
        <div className={b()}>
            <div className={b('body')}>
                <h3 className={b('title')}>{title}</h3>
                <p className={b('text')}>{description}</p>
            </div>
            <Button size='m' view='dl-light' onClick={() => hideModal()} width='max'>
                {i18n.buttonText}
            </Button>
        </div>
    );
}

interface MobileParams extends Omit<MobileMessageProps, 'hideModal'> {
    beforeShow?: () => void;
    level?: ModalLevel;
}

export function useMobileMessage(): [
    (params: MobileParams) => void,
    (beforeClose?: () => false | void) => void
] {
    const [showModal, hideModal] = useModal();
    const device = useDevice();

    const show = useCallback((params: MobileParams) => {
        if (device !== Device.Mobile) {
            return;
        }

        const {beforeShow, level, ...rest} = params;

        beforeShow?.();

        showModal(<MobileMessage
            {...rest}
            hideModal={hideModal}
        />, {
            className: b('dl-mobile'),
            hideByOutsideClick: false,
            level
        });
    }, [device, showModal, hideModal]);

    return [show, hideModal];
}

export function useShowOnlyDesktop(): [
    (mobile: MobileParams, showOnDesktop?: () => void) => void,
    (beforeClose?: () => false | void) => void
] {
    const device = useDevice();
    const [showMobileMessage, hideMobileMessage] = useMobileMessage();

    const show = useCallback((
        mobile: MobileParams,
        showOnDesktop?: () => void
    ) => {
        if (device !== Device.Mobile) {
            showOnDesktop?.();

            return;
        }

        showMobileMessage(mobile);
    }, [device, showMobileMessage]);

    return [show, hideMobileMessage];
}
